import classNames from 'classnames';
import gql from 'graphql-tag';
import React from 'react';
import { Calendar, MapPin } from 'react-feather';
import { useIntl } from 'react-intl';
import IconCircle from '../../../legacyCore/icons/IconCircle';
import { DATE_FORMAT, DATE_TIME_FORMAT, TIME_FORMAT } from '../../constants';
import { formatDate } from '../../utils/formatCalendarTime';
import getTimeZoneFormat from '../../utils/getTimeZoneFormat';
import { getOfficeBrandName } from '../../utils/officeBranding';
import style from './AppointmentFormInfo.scss';

interface AppointmentFormInfoProps {
  provider: {
    display: string;
    profile: {
      photo: string;
      photo2x: string;
    };
    office: {
      display: string;
      name: string;
      messagingName: string | null;
      address1: string;
      address2: string | null;
      city: string;
      state: string;
      postalCode: string;
      latitude: string;
      longitude: string;
      timeZoneCode: string;
      group: {
        name: string;
      } | null;
      practice: {
        display: string;
      };
    };
  };
  opening: {
    time: string;
  };
  large?: boolean;
}

export default function AppointmentFormInfo({
  provider,
  opening,
  large,
}: AppointmentFormInfoProps) {
  const intl = useIntl();
  const { profile, office } = provider;
  const timeZone = getTimeZoneFormat(office.timeZoneCode);
  const brandName = getOfficeBrandName(office);
  return (
    <div className={classNames(style.info, large && style['info--large'])}>
      <div className={style.info__row}>
        <div className={style.info__dt}>
          <img
            className={style.info__photo}
            src={profile.photo}
            srcSet={`${profile.photo2x} 2x`}
            alt="Provider"
          />
        </div>
        <div className={style.info__dd}>
          <h2 className={style.info__h1}>{provider.display}</h2>
        </div>
      </div>

      <div className={classNames(style.info__row, style['info__row--border'])}>
        <div className={style.info__dt}>
          <IconCircle className={style['info__icon-circle']} color="red" size={1.5}>
            <Calendar aria-label="Date Time" />
          </IconCircle>
        </div>
        <div className={style.info__dd}>
          <time
            dateTime={opening.time}
            title={formatDate(intl, opening.time, timeZone, {
              intl: DATE_TIME_FORMAT.FULL,
              fallback: DATE_TIME_FORMAT.FULL_FALLBACK,
            })}
          >
            <span className={style.info__h2}>
              {formatDate(intl, opening.time, timeZone, {
                intl: DATE_FORMAT.LONG,
                fallback: DATE_FORMAT.LONG_FALLBACK,
              })}
            </span>
            <span className={style.info__line}>
              {formatDate(intl, opening.time, timeZone, {
                intl: TIME_FORMAT.LONG,
                fallback: TIME_FORMAT.LONG_FALLBACK,
              })}
            </span>
          </time>
        </div>
      </div>

      <div className={style.info__row}>
        <div className={style.info__dt}>
          <IconCircle className={style['info__icon-circle']} color="red" size={1.5}>
            <MapPin aria-label="Location" />
          </IconCircle>
        </div>
        <div className={style.info__dd}>
          {brandName && <span className={style.info__h2}>{brandName}</span>}
          <span className={brandName ? style.info__line : style.info__h2}>{office.address1}</span>
          {office.address2 && <span className={style.info__line}>{office.address2}</span>}
          <span className={style.info__line}>
            {office.city}, {office.state} {office.postalCode}
          </span>
        </div>
      </div>
    </div>
  );
}

export const AppointmentFormInfoProviderFragment = {
  name: 'AppointmentFormInfoProvider',
  query: gql`
    fragment AppointmentFormInfoProvider on Provider {
      id
      display
      profile {
        id
        photo: photo(size: 72)
        photo2x: photo(size: 144)
      }
      office {
        id
        name
        messagingName
        address1
        address2
        city
        state
        postalCode
        latitude
        longitude
        timeZoneCode
        group {
          id
          name
        }
        practice {
          id
          display
        }
      }
    }
  `,
};

export const AppointmentFormInfoOpeningFragment = {
  name: 'AppointmentFormInfoOpening',
  query: gql`
    fragment AppointmentFormInfoOpening on Opening {
      id
      time
    }
  `,
};
